import React, { useEffect, useState } from "react";
import { Sparkles, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1599847987657-881f11b92a75";

// answer option -> skin profile bucket
const MAP = {
  q1: ["dry", "oily", "balanced"],
  q2: ["dry", "balanced", "balanced"],
  q3: ["dry", "oily", "balanced"],
};
const RESULT = {
  dry: {
    title: { it: "Pelle secca · idratazione intensa", en: "Dry skin · intense hydration" },
    slugs: ["crema-nutriente", "olio-rigenerante", "crema-corpo-seta"],
  },
  oily: {
    title: { it: "Pelle mista/grassa · freschezza e purezza", en: "Combination/oily · freshness & purity" },
    slugs: ["detergente-puro", "siero-vitalita", "shampoo-delicato"],
  },
  balanced: {
    title: { it: "Pelle equilibrata · luminosità e prevenzione", en: "Balanced skin · radiance & prevention" },
    slugs: ["siero-vitalita", "crema-nutriente", "maschera-capelli"],
  },
};

const SkinTest = () => {
  const { t, tl } = useLang();
  const [step, setStep] = useState(0); // 0 intro, 1-3 questions, 4 result
  const [answers, setAnswers] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products?category=skincare").then((r) => setProducts(r.data));
  }, []);

  const questions = [1, 2, 3].map((n) => ({
    q: t(`skin.q${n}`),
    key: `q${n}`,
    options: [t(`skin.q${n}a`), t(`skin.q${n}b`), t(`skin.q${n}c`)],
  }));

  const answer = (qKey, idx) => {
    const next = [...answers, MAP[qKey][idx]];
    setAnswers(next);
    setStep((s) => s + 1);
  };

  const computeBucket = () => {
    const count = { dry: 0, oily: 0, balanced: 0 };
    answers.forEach((a) => (count[a] += 1));
    let best = "balanced";
    let max = -1;
    ["dry", "oily", "balanced"].forEach((k) => {
      if (count[k] > max) {
        max = count[k];
        best = k;
      }
    });
    return best;
  };

  const restart = () => {
    setAnswers([]);
    setStep(0);
  };

  const bucket = step >= 4 ? computeBucket() : null;
  const recommended = bucket
    ? RESULT[bucket].slugs.map((s) => products.find((p) => p.slug === s)).filter(Boolean)
    : [];

  return (
    <div data-testid="skintest-page" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <Reveal>
        <div className="text-center max-w-xl mx-auto">
          <div className="w-12 h-12 border border-champagne rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles size={22} strokeWidth={1.5} className="text-champagne" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-charcoal">{t("skin.title")}</h1>
          <p className="text-stone-500 mt-4">{t("skin.subtitle")}</p>
        </div>
      </Reveal>

      <div className="mt-14 max-w-xl mx-auto">
        {step === 0 && (
          <div className="text-center">
            <button
              onClick={() => setStep(1)}
              data-testid="skin-start"
              className="bg-charcoal text-white px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-stone-800 transition-colors"
            >
              {t("skin.start")}
            </button>
          </div>
        )}

        {step >= 1 && step <= 3 && (
          <div data-testid={`skin-question-${step}`}>
            <p className="text-xs uppercase tracking-[0.2em] text-champagne mb-3">{step} / 3</p>
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-8">{questions[step - 1].q}</h2>
            <div className="space-y-3">
              {questions[step - 1].options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => answer(questions[step - 1].key, i)}
                  data-testid={`skin-answer-${step}-${i}`}
                  className="w-full text-left border border-stone-300 px-6 py-4 hover:border-charcoal hover:bg-white transition-colors text-charcoal"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {step >= 4 && (
        <div data-testid="skin-result" className="mt-6">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-champagne mb-3">{t("skin.result")}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal">{tl(RESULT[bucket].title)}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-10 mt-12">
            {recommended.map((p, i) => (
              <Reveal key={p.slug} delay={i * 100}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <button
              onClick={restart}
              data-testid="skin-restart"
              className="inline-flex items-center gap-2 border-b border-stone-400 pb-1 text-xs uppercase tracking-[0.2em] text-stone-600 hover:text-charcoal hover:border-charcoal transition-colors"
            >
              <RotateCcw size={14} /> {t("skin.restart")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkinTest;
